import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import Editor from '@monaco-editor/react';
import Ajv2020 from 'ajv/dist/2020';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import flowDefinitionV2Schema from '../../../../packages/builder-schema/schemas/flow-definition.v2.schema.json';
import appDefinitionV2Schema from '../../../../packages/builder-schema/schemas/app-definition.v2.schema.json';

function compileValidator(schema) {
  const ajv = new Ajv2020({ allErrors: true, strict: false });
  return ajv.compile(schema);
}

function computeEditorHeight() {
  if (typeof window === 'undefined') return 480;
  return Math.min(Math.round(window.innerHeight * 0.55), 560);
}

/**
 * JSON editor for V2 flow/app definitions with Draft 2020-12 validation
 * against `packages/builder-schema` (same source as CI and the VS Code extension).
 */
export default function StudioV2JsonMonacoEditor({
  value,
  onChange,
  schemaKind,
  active = true,
}) {
  const theme = useTheme();
  const schema = schemaKind === 'flow' ? flowDefinitionV2Schema : appDefinitionV2Schema;
  const validate = useMemo(() => compileValidator(schema), [schema]);
  const [issues, setIssues] = useState([]);
  const [editorHeight, setEditorHeight] = useState(computeEditorHeight);
  const debounceRef = useRef(null);
  const editorRef = useRef(null);

  useEffect(() => {
    const onResize = () => setEditorHeight(computeEditorHeight());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    const ed = editorRef.current;
    if (!ed) return undefined;
    const frame = window.requestAnimationFrame(() => ed.layout());
    return () => window.cancelAnimationFrame(frame);
  }, [active, editorHeight]);

  const runValidate = useCallback((text) => {
    let data;
    try {
      data = JSON.parse(text);
    } catch (e) {
      setIssues([{ path: '/', message: `Invalid JSON: ${e.message}` }]);
      return;
    }
    const ok = validate(data);
    if (!ok) {
      setIssues((validate.errors || []).map((er) => ({
        path: er.instancePath || '/',
        message: er.message || 'validation error',
      })));
      return;
    }
    setIssues([]);
  }, [validate]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runValidate(value), 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, runValidate]);

  const editorTheme = theme.palette.mode === 'dark' ? 'vs-dark' : 'vs';

  const onFormat = useCallback(() => {
    const ed = editorRef.current;
    if (!ed) return;
    ed.getAction('editor.action.formatDocument')?.run();
    const next = ed.getValue();
    if (next != null && next !== value) onChange(next);
  }, [onChange, value]);

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 0.5 }}>
        <Button size="small" variant="outlined" onClick={onFormat} sx={{ textTransform: 'none' }}>
          Format JSON
        </Button>
      </Stack>
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          minHeight: 360,
          height: editorHeight,
          '& .monaco-editor': { paddingTop: 0 },
        }}
      >
        <Editor
          height={editorHeight}
          defaultLanguage="json"
          theme={editorTheme}
          value={value || ''}
          loading={(
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1,
            }}
            >
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">Loading editor…</Typography>
            </Box>
          )}
          onChange={(v) => onChange(v ?? '')}
          onMount={(editor) => {
            editorRef.current = editor;
            editor.layout();
          }}
          options={{
            minimap: { enabled: true },
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            tabSize: 2,
            formatOnPaste: true,
            automaticLayout: true,
          }}
        />
      </Box>
      {issues.length > 0 ? (
        <Alert severity="warning" sx={{ mt: 1.5 }} icon={false}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Schema validation</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 0 }}>
            {issues.slice(0, 12).map((it, i) => (
              <Typography key={`${it.path}-${i}`} component="li" variant="caption" sx={{ display: 'list-item' }}>
                <strong>{it.path}</strong>
                {' — '}
                {it.message}
              </Typography>
            ))}
          </Box>
          {issues.length > 12 ? (
            <Typography variant="caption" color="text.secondary">
              …and
              {' '}
              {issues.length - 12}
              {' '}
              more
            </Typography>
          ) : null}
        </Alert>
      ) : (
        <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
          Matches
          {' '}
          {schemaKind === 'flow' ? 'flow-definition.v2' : 'app-definition.v2'}
          {' '}
          schema.
        </Typography>
      )}
    </Box>
  );
}
